import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, Square, RotateCcw, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Perform Mode — a distraction-free stage teleprompter.
 * Big lyric text, optional backing track auto-plays, countdown before you go,
 * scrolls smoothly through the song. At the end, self-rate how it felt.
 */
export default function PerformMode({ song }) {
  const lines = useMemo(
    () => (song?.lyrics || "")
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l && !/^\[[^\]]+\]$/.test(l)),
    [song]
  );
  const [state, setState] = useState("ready"); // ready | countdown | performing | done
  const [countdown, setCountdown] = useState(3);
  const [seconds, setSeconds] = useState(0);
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const performTimerRef = useRef(null);
  const scrollTimerRef = useRef(null);

  const hasBackingTrack = !!song?.backing_track_path;
  const backingSrc = hasBackingTrack ? `${API_BASE}/songs/${song.id}/backing-track` : "";

  const cleanup = () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (performTimerRef.current) clearInterval(performTimerRef.current);
    if (scrollTimerRef.current) clearInterval(scrollTimerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  };

  useEffect(() => () => cleanup(), []);

  const start = () => {
    cleanup();
    setState("countdown");
    setCountdown(3);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownTimerRef.current);
          beginPerform();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const beginPerform = async () => {
    setState("performing");
    setSeconds(0);
    // Start backing track if available
    if (audioRef.current && backingSrc) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (err) { console.warn("perform backing autoplay blocked", err); }
    }
    // Elapsed clock
    performTimerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    // Auto-scroll teleprompter — gentle continuous scroll based on estimated song duration
    const durationEstimate = Math.max(30, lines.length * 3); // ~3s per line default
    const el = containerRef.current;
    if (el) {
      el.scrollTop = 0;
      const total = el.scrollHeight - el.clientHeight;
      const stepMs = 40; // 25fps
      const steps = (durationEstimate * 1000) / stepMs;
      const perStep = total / Math.max(steps, 1);
      scrollTimerRef.current = setInterval(() => {
        if (!containerRef.current) return;
        containerRef.current.scrollTop += perStep;
        if (containerRef.current.scrollTop >= total) {
          clearInterval(scrollTimerRef.current);
        }
      }, stepMs);
    }
  };

  const stop = () => {
    cleanup();
    setState("done");
  };

  const restart = () => {
    cleanup();
    setState("ready");
    setCountdown(3);
    setSeconds(0);
    if (containerRef.current) containerRef.current.scrollTop = 0;
  };

  const record = async (accuracyGuess) => {
    try {
      const { data } = await api.post(`/songs/${song.id}/practice`, {
        mode: "perform",
        accuracy: accuracyGuess,
        section_name: "Full song",
        combo: 0,
      });
      toast.success(`Performance logged · +${data.xp_gain} XP`);
      if (accuracyGuess >= 0.9) celebrate("big");
      if (data.unlocked?.length) data.unlocked.forEach((a) => toast(`Unlocked: ${a.title}`, { description: a.desc }));
    } catch (e) { toast.error(formatApiError(e)); }
    setState("ready");
    if (containerRef.current) containerRef.current.scrollTop = 0;
    setSeconds(0);
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div data-testid="perform-mode">
      {/* Backing audio */}
      {backingSrc && <audio ref={audioRef} src={backingSrc} preload="auto" className="hidden" data-testid="perform-backing-audio" />}

      {/* Header controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="overline-label text-accent">Perform</p>
          <p className="text-sm text-muted-foreground">
            Stage view: big lyrics, {hasBackingTrack ? "backing track auto-plays" : "no backing track yet — upload one on Song Detail"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {state !== "performing" ? (
            <Button onClick={start} className="rounded-full h-11 px-6 glow-amber-sm hover:glow-amber transition-shadow"
                    data-testid="perform-start-btn" disabled={state === "countdown"}>
              <Play size={16} className="mr-1.5" /> {state === "countdown" ? `Starting in ${countdown}` : "Take the stage"}
            </Button>
          ) : (
            <>
              <span className="font-mono text-lg tabular-nums" data-testid="perform-timer">{mm}:{ss}</span>
              <Button onClick={stop} variant="destructive" className="rounded-full h-11 px-5"
                      data-testid="perform-stop-btn">
                <Square size={16} className="mr-1.5" /> End
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Countdown */}
      {state === "countdown" && (
        <div className="text-center py-16" data-testid="perform-countdown">
          <p className="overline-label text-accent mb-3">Deep breath</p>
          <p className="font-display text-9xl text-primary glow-amber" data-testid="perform-countdown-num">{countdown}</p>
        </div>
      )}

      {/* Teleprompter */}
      {(state === "ready" || state === "performing") && (
        <div
          ref={containerRef}
          data-testid="perform-teleprompter"
          className="grain-panel border border-border rounded-2xl bg-card p-8 md:p-14 h-[70vh] overflow-y-auto fade-mask-b"
          style={{ scrollBehavior: "smooth" }}
        >
          <div className="space-y-6 pb-40">
            {lines.map((l, i) => (
              <p key={`${i}-${l.slice(0, 10)}`} className="lyric-line text-3xl md:text-5xl lg:text-6xl leading-[1.15] text-foreground/90 hover:text-foreground transition-colors">
                {l}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Self-rating after the run */}
      {state === "done" && (
        <div className="text-center py-12" data-testid="perform-done">
          <p className="overline-label text-accent">How did it feel?</p>
          <h3 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">Rate this take.</h3>
          <p className="mt-2 text-muted-foreground">Your honest self-rating tunes the schedule for next time.</p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Button onClick={() => record(1.0)} className="rounded-full h-12 px-6 glow-amber-sm"
                    data-testid="perform-rate-nailed">
              <ThumbsUp size={16} className="mr-1.5" /> Nailed it
            </Button>
            <Button onClick={() => record(0.7)} variant="outline" className="rounded-full h-12 px-6"
                    data-testid="perform-rate-mostly">
              Mostly there
            </Button>
            <Button onClick={() => record(0.4)} variant="outline" className="rounded-full h-12 px-6"
                    data-testid="perform-rate-shaky">
              <ThumbsDown size={16} className="mr-1.5" /> Shaky
            </Button>
          </div>
          <button onClick={restart}
                  className="mt-8 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                  data-testid="perform-restart-btn">
            <RotateCcw size={14} /> Take it again
          </button>
        </div>
      )}
    </div>
  );
}
